import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Engine, emptySnapshot, roleCan, type CapKey, type Snapshot } from '@piaip/shared';
import { ApiError, api, type Me } from './api';

interface AppState {
  ready: boolean;
  me: Me | null;
  orgName: string;
  snapshot: Snapshot;
  engine: Engine;
  /** Bumped whenever the snapshot is replaced, so views can reset local buffers. */
  revision: number;
  saving: boolean;
  toast: { text: string; tone: 'ok' | 'bad' } | null;
  can: (cap: CapKey) => boolean;
  refresh: () => Promise<void>;
  signIn: (id: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  setMe: (me: Me) => void;
  notify: (text: string, tone?: 'ok' | 'bad') => void;
  /** Runs a mutation, shows any error, and refreshes the snapshot when it succeeds. */
  mutate: <T>(fn: () => Promise<T>, opts?: { refresh?: boolean }) => Promise<T | null>;
}

const Ctx = createContext<AppState | null>(null);

export function useApp(): AppState {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useApp must be used inside <AppProvider>');
  return ctx;
}

const REFRESH_MS = 30_000;

export function AppProvider({ children }: { children: ReactNode }) {
  const [ready, setReady] = useState(false);
  const [me, setMe] = useState<Me | null>(null);
  const [orgName, setOrgName] = useState('Irrigation Department, Government of the Punjab');
  const [snapshot, setSnapshot] = useState<Snapshot>(() => emptySnapshot());
  const [revision, setRevision] = useState(0);
  const [saving, setSaving] = useState(0);
  const [toast, setToast] = useState<{ text: string; tone: 'ok' | 'bad' } | null>(null);
  const toastTimer = useRef<number | undefined>(undefined);

  const notify = useCallback((text: string, tone: 'ok' | 'bad' = 'ok') => {
    setToast({ text, tone });
    window.clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToast(null), tone === 'bad' ? 7000 : 3500);
  }, []);

  const refresh = useCallback(async () => {
    try {
      const { snapshot } = await api.snapshot();
      setSnapshot(snapshot);
      setRevision((r) => r + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setMe(null);
        setSnapshot(emptySnapshot());
      } else {
        notify(err instanceof Error ? err.message : 'The scheme data could not be loaded.', 'bad');
      }
    }
  }, [notify]);

  // Boot: identify the visitor, then load the scheme if they are signed in.
  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const [cfg, who] = await Promise.all([
          api.config().catch(() => null),
          api.me().catch(() => ({ user: null })),
        ]);
        if (!live) return;
        if (cfg?.orgName) setOrgName(cfg.orgName);
        setMe(who.user);
        if (who.user) await refresh();
      } finally {
        if (live) setReady(true);
      }
    })();
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Other people are entering data on their own machines, so the picture is
  // re-read on a timer and whenever this window is brought back to the front.
  useEffect(() => {
    if (!me) return;
    const tick = () => {
      if (document.visibilityState === 'visible') void refresh();
    };
    const id = window.setInterval(tick, REFRESH_MS);
    window.addEventListener('focus', tick);
    return () => {
      window.clearInterval(id);
      window.removeEventListener('focus', tick);
    };
  }, [me, refresh]);

  const signIn = useCallback(
    async (id: string, password: string) => {
      const { user } = await api.login(id, password);
      setMe(user);
      await refresh();
    },
    [refresh],
  );

  const signOut = useCallback(async () => {
    await api.logout().catch(() => undefined);
    setMe(null);
    setSnapshot(emptySnapshot());
  }, []);

  const mutate = useCallback(
    async <T,>(fn: () => Promise<T>, opts: { refresh?: boolean } = {}) => {
      setSaving((n) => n + 1);
      try {
        const out = await fn();
        if (opts.refresh !== false) await refresh();
        return out;
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) {
          setMe(null);
          notify('Your session has ended. Sign in again.', 'bad');
        } else {
          notify(err instanceof Error ? err.message : 'That change could not be saved.', 'bad');
          // Pull the authoritative picture back so the screen never lies.
          await refresh();
        }
        return null;
      } finally {
        setSaving((n) => n - 1);
      }
    },
    [notify, refresh],
  );

  const engine = useMemo(() => new Engine(snapshot), [snapshot]);
  const can = useCallback((cap: CapKey) => roleCan(me?.role, cap), [me]);

  const value = useMemo<AppState>(
    () => ({
      ready,
      me,
      orgName,
      snapshot,
      engine,
      revision,
      saving: saving > 0,
      toast,
      can,
      refresh,
      signIn,
      signOut,
      setMe,
      notify,
      mutate,
    }),
    [ready, me, orgName, snapshot, engine, revision, saving, toast, can, refresh, signIn, signOut, notify, mutate],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
